"use client"

import { useState } from "react"
import { ArrowRight, BookOpen, Cable, GraduationCap, ListChecks, Plug, Rocket } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import { CONNECTORS, type ConnectorMeta } from "@/modules/lernpfad/data/connectors"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import ConnectWizard from "./connect-wizard"
import { SectionHeading } from "../ui-bits"
import { ViewHeader } from "@/modules/lernpfad/components/ui-bits"

/**
 * The 4th view: connections. One card per connector, capability badges from the
 * `LmsPort.supports` design (docs/lms-integration.md §5), and the connect
 * wizard for Moodle. Everything simulated; the vocabulary is the real one.
 */

const CAPABILITY_META = [
    { key: "catalog" as const, labelKey: "cap.catalog", icon: BookOpen },
    { key: "transcript" as const, labelKey: "cap.transcript", icon: ListChecks },
    { key: "launch" as const, labelKey: "cap.launch", icon: Rocket },
    { key: "assign" as const, labelKey: "cap.assign", icon: GraduationCap },
]

export default function IntegrationsView({ state }: { state: LernpfadDemoState }) {
    const { localizer, connections, setConnection, openLms } = state
    const { t } = localizer
    const [wizardOpen, setWizardOpen] = useState(false)

    const connect = (connector: ConnectorMeta) => {
        if (connector.id === "moodle") {
            setWizardOpen(true)
            return
        }
        // csv: instant "connect" — a file import has no discovery ceremony.
        setConnection(connector.id, { status: "connected", syncedCourses: 0 })
    }

    return (
        <div className="space-y-8">
            <ViewHeader title={t("int.title")} subtitle={t("int.subtitle")} />

            <section>
                <SectionHeading icon={Cable} title={t("int.tab")} />
                <div className="grid gap-4 sm:grid-cols-2">
                    {CONNECTORS.map((connector) => {
                        const connection = connections[connector.id]
                        const isConnected = connection?.status === "connected"

                        return (
                            <div
                                key={connector.id}
                                className={cn(
                                    "flex flex-col lp-card rounded-xl border bg-card p-4 lp-card-hover",
                                    isConnected && "border-primary/40",
                                )}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h3 className="font-semibold">{connector.name}</h3>
                                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                            {connector.detail}
                                        </p>
                                    </div>
                                    <span
                                        className={cn(
                                            "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                                            isConnected
                                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                                : connector.availability === "coming"
                                                  ? "bg-muted text-muted-foreground"
                                                  : "bg-primary/10 text-primary",
                                        )}
                                    >
                                        {isConnected
                                            ? t("int.connected")
                                            : connector.availability === "coming"
                                              ? t("int.coming")
                                              : t("int.available")}
                                    </span>
                                </div>

                                {/* Capability badges — the four surfaces. */}
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {CAPABILITY_META.map(({ key, labelKey, icon: Icon }) => (
                                        <span
                                            key={key}
                                            className={cn(
                                                "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs",
                                                connector.supports[key]
                                                    ? "text-foreground"
                                                    : "border-dashed text-muted-foreground/50 line-through",
                                            )}
                                        >
                                            <Icon className="size-2.5" />
                                            {t(labelKey as never)}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-4 flex items-center justify-between gap-2 border-t pt-3">
                                    {isConnected ? (
                                        <>
                                            <span className="text-xs text-muted-foreground">
                                                {connection?.syncedCourses
                                                    ? `${t("int.courses", { count: connection.syncedCourses })} · `
                                                    : ""}
                                                {t("int.lastSync", { when: t("int.justNow") })}
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setConnection(connector.id, null)}
                                            >
                                                {t("int.disconnect")}
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant={connector.id === "moodle" ? "default" : "outline"}
                                            disabled={connector.availability !== "connectable"}
                                            onClick={() => connect(connector)}
                                            className="ml-auto"
                                        >
                                            <Plug className="size-3.5" />
                                            {t("int.connect")}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </section>

            {/* The payoff of the whole view: not just "these systems exist",
                but what an employee actually sees inside the one Compass just
                launched into. A different chrome, on purpose — see
                `lms/lms-shell.tsx`. */}
            <section>
                <button
                    type="button"
                    onClick={() => openLms()}
                    className="flex w-full flex-col items-start gap-3 lp-card rounded-xl border bg-gradient-to-br from-primary/[0.06] to-transparent p-5 text-left lp-card-hover sm:flex-row sm:items-center sm:justify-between"
                >
                    <div>
                        <h3 className="flex items-center gap-2 font-semibold">
                            <GraduationCap className="size-4 text-primary" />
                            {t("lms.integrations.preview.title")}
                        </h3>
                        <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                            {t("lms.integrations.preview.body")}
                        </p>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
                        {t("lms.integrations.preview.cta")}
                        <ArrowRight className="size-4" />
                    </span>
                </button>
            </section>

            <ConnectWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onConnected={(syncedCourses) =>
                    setConnection("moodle", { status: "connected", syncedCourses, freshlySynced: true })
                }
                localizer={localizer}
            />
        </div>
    )
}
